import React, { useDeferredValue, useMemo } from 'react'
import PropTypes from 'prop-types'
import { AGE_BASIS, TARGETING_ATTRIBUTE } from '../constants'
import { buildPlainLanguageFooter, buildPlainLanguageSegments } from '../summaries/plainLanguage'

const targetingAudience = (rows = [], entityOptions) => {
  const nameOf = (list, id) => (list || []).find(item => String(item.id) === String(id))?.name
  const bits = []
  const arm = rows.find(row => row.attribute === TARGETING_ATTRIBUTE.arm)
  const phase = rows.find(row => row.attribute === TARGETING_ATTRIBUTE.periodPhase)
  const age = rows.find(row => row.attribute === TARGETING_ATTRIBUTE.age)
  if (arm?.value) bits.push(nameOf(entityOptions.arms, arm.value))
  if (phase?.value) {
    const name = nameOf(entityOptions.phases, phase.value) || 'a'
    bits.push(/phase/i.test(name) ? name : `${name} phase`)
  }
  if (age?.value) {
    const when = age.ageBasis === AGE_BASIS.current ? 'currently' : 'at enrollment'
    bits.push(`aged ${age.value}+ ${when}`)
  }
  return bits.join(' · ')
}

const ScheduleSummary = ({ schedule, scheduleIndex, entityOptions, instrumentName, scheduleCount }) => {
  const deferredSchedule = useDeferredValue(schedule)
  const nameForAnchor = anchor => {
    const lists = {
      arm: entityOptions.arms,
      phase: entityOptions.phases,
      visit_cycle: entityOptions.cycles,
      visit: entityOptions.visits,
      track: entityOptions.tracks,
      chapter: entityOptions.chapters,
      instrument: entityOptions.instruments,
    }
    const ids = anchor?.instanceIds?.length ? anchor.instanceIds : [anchor?.instanceId]
    return ids
      .map(id => (lists[anchor?.type] || []).find(item => String(item.id) === String(id))?.name)
      .filter(Boolean)
      .join(', ')
  }
  const segments = useMemo(
    () =>
      buildPlainLanguageSegments(deferredSchedule, {
        nameForAnchor,
        instrumentName,
        audience: targetingAudience(deferredSchedule.targeting, entityOptions),
      }),
    [deferredSchedule, entityOptions, instrumentName],
  )
  const footer = buildPlainLanguageFooter({
    instancePermissions: deferredSchedule.instancePermissions,
    scheduleIndex,
    scheduleCount,
  })

  return (
    <aside className='se-plain-language'>
      <span className='se-plain-language-label'>Schedule {scheduleIndex + 1} in plain language</span>
      <p className='se-plain-language-body'>
        {segments.map((part, index) =>
          part.emphasize ? <strong key={`${part.text}-${index}`}>{part.text}</strong> : part.text,
        )}
      </p>
      {footer ? <p className='se-plain-language-footer'>{footer}</p> : null}
    </aside>
  )
}

ScheduleSummary.propTypes = {
  schedule: PropTypes.object,
  scheduleIndex: PropTypes.number,
  entityOptions: PropTypes.object,
  instrumentName: PropTypes.string,
  scheduleCount: PropTypes.number,
}

export default ScheduleSummary
